/**
 * Read-time transcript → shot interval overlap for CCA shot timeline.
 * Mirrors backend competitive_content_shot_transcript.py — no second ASR.
 */

export type TimedTranscriptSegment = {
  start_ms: number
  end_ms: number
  text: string
}

export type TimedShot = {
  start_ms: number
  end_ms: number
  transcript_text?: string | null
}

export function intervalsOverlap(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  return Math.max(0, Math.min(aEnd, bEnd) - Math.max(aStart, bStart)) > 0
}

export function composeTranscriptForShot(
  shotStartMs: number,
  shotEndMs: number,
  segments: TimedTranscriptSegment[],
): string | null {
  const pieces: Array<{ start: number; text: string }> = []
  for (const seg of segments) {
    const text = (seg.text || '').trim()
    if (!text) continue
    if (!intervalsOverlap(shotStartMs, shotEndMs, seg.start_ms, seg.end_ms)) continue
    pieces.push({ start: seg.start_ms, text })
  }
  if (!pieces.length) return null
  pieces.sort((a, b) => a.start - b.start)
  const joined: string[] = []
  for (const p of pieces) {
    if (joined.length && joined[joined.length - 1] === p.text) continue
    joined.push(p.text)
  }
  return joined.length ? joined.join(' ') : null
}

/** Prefer API projection; fall back to client overlap on detail.transcript. */
export function resolveShotTranscriptText(
  shot: TimedShot,
  segments: TimedTranscriptSegment[],
): string | null {
  if (typeof shot.transcript_text === 'string') {
    const trimmed = shot.transcript_text.trim()
    return trimmed || null
  }
  return composeTranscriptForShot(shot.start_ms, shot.end_ms, segments)
}

export function formatScaleMotion(shotScale: string | null | undefined, cameraMotion: string | null | undefined): string {
  const parts = [shotScale, cameraMotion].map((v) => (v || '').trim()).filter(Boolean)
  return parts.length ? parts.join(' · ') : '—'
}
